import { AnyAction, createSlice, Reducer, Slice } from '@reduxjs/toolkit';

import { PaginationDefaults } from '../../../../shared/utils/pagination-defaults.util';
import { getWebGamesTotalCount } from '../../thunks/get-web-games-total-count.thunk';
import { getWebGames } from '../../thunks/get-web-games.thunk';
import { WebGameHeaderFiltersAndPaginationModel } from '../../thunks/models/web-game-header-filters-and-pagination.model';
import { WebGamesStateModel } from '../models/web-games-state.model';
import { WebGamesReducers } from '../types/web-games-reducers.type';

const filtersInitialState: WebGameHeaderFiltersAndPaginationModel = {
  header: {
    title: '',
  },
  pagination: {
    totalCount: PaginationDefaults.totalCount,
    pageNumber: PaginationDefaults.pageNumber,
    size: PaginationDefaults.size,
    offset: PaginationDefaults.offset,
  },
};

const initialState: WebGamesStateModel = {
  webGames: [],
  filters: filtersInitialState,
  data: null,
  thumbnailFileId: null,
  zipFileId: null,
};

export const webGamesSlice: Slice<
  WebGamesStateModel,
  WebGamesReducers,
  'webGames'
> = createSlice({
  name: 'webGames',
  initialState,
  reducers: {
    addPagination(state, action) {
      state.filters.pagination.size = action.payload.size;
      state.filters.pagination.offset = action.payload.offset;
      state.filters.pagination.pageNumber = action.payload.pageNumber;
    },

    resetPagination(state, action) {
      state.filters.pagination.offset = PaginationDefaults.offset;
      state.filters.pagination.size = action.payload.size;
      state.filters.pagination.pageNumber = PaginationDefaults.pageNumber;
    },

    filterGamesByTitle(state, action) {
      state.filters.header = {
        ...state.filters.header,
        title: action.payload.filterValue,
      };
    },

    setGame(state, action) {
      state.data = action.payload;
    },

    resetGameForm(state) {
      state.data = null;
    },

    resetGame(state) {
      Object.assign(state, initialState);
    },

    setGameZip(state, action) {
      state.zipFileId = action.payload.id;
    },

    setGameThumbnail(state, action) {
      state.thumbnailFileId = action.payload.id;
    },

    resetGameThumbnail(state) {
      state.thumbnailFileId = null;
    },

    resetGameZip(state) {
      state.zipFileId = null;
    },
  },

  extraReducers: builder => {
    builder
      .addCase(getWebGames.fulfilled, (state, action) => {
        state.webGames = action.payload;
      })
      .addCase(getWebGamesTotalCount.fulfilled, (state, action) => {
        state.filters.pagination.totalCount = action.payload;
      });
  },
});

export const {
  filterGamesByTitle,
  resetPagination,
  addPagination,
  setGame,
  resetGame,
  setGameThumbnail,
  setGameZip,
  resetGameThumbnail,
  resetGameZip,
  resetGameForm,
} = webGamesSlice.actions;

const webGamesSliceReducers: Reducer<WebGamesStateModel, AnyAction> =
  webGamesSlice.reducer;

export default webGamesSliceReducers;
